import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";
import "../styles/night-legal.css";

const updatedAt = "June 27, 2026";

const sections = [
  {
    title: "Personal information we collect",
    body: [
      "We collect account and contact information such as name, email address, phone number, company name, artist name, label name, role, login details, account preferences, and communications with BNE Studio.",
      "We collect artist, label, catalog, rights, release, contributor, metadata, split, recoupment, report, payout, contract, event, link, lyric, support, and operational information you submit or generate while using BNE Studio.",
      "We collect payment, tax, identity, payout, and compliance information when needed to verify accounts, process payments, meet tax obligations, prevent fraud, and administer royalty or revenue workflows.",
      "We collect files and content you provide, including recordings, artwork, stems, videos, lyrics, documents, images, names, likenesses, credits, contracts, notes, comments, and other materials submitted through the platform.",
    ],
  },
  {
    title: "Technical and usage information",
    body: [
      "We collect technical information such as IP address, device type, browser, operating system, identifiers, approximate location, referring pages, pages viewed, session activity, timestamps, diagnostics, crash data, performance data, and security logs.",
      "We use cookies, local storage, pixels, SDKs, and similar technologies for authentication, account security, preferences, analytics, diagnostics, fraud prevention, support, and product improvement.",
      "Some information is collected automatically when you visit bne.studio, open emails, use dashboard features, upload files, interact with forms, or connect with related BNE Studio products and services.",
    ],
  },
  {
    title: "Sources of information",
    body: [
      "We collect information directly from you, from users or team members acting through your account, from collaborators or invited recipients, from stores and distribution partners, from payment and verification providers, from analytics and security tools, and from public or commercially available sources when needed to administer rights, support, compliance, or fraud review.",
      "We may receive information from digital service providers, streaming platforms, social platforms, content identification systems, payment processors, tax providers, support vendors, advertising or analytics providers, and business partners.",
    ],
  },
  {
    title: "How we use information",
    body: [
      "We use information to provide, operate, secure, maintain, personalize, and improve BNE Studio, including account access, catalog administration, distribution, reporting, payouts, contracts, tools, links, support, compliance, and communications.",
      "We use information to process releases, deliver content to partners, manage metadata, administer royalties and revenue, handle takedowns, resolve rights claims, verify identity and tax information, prevent fraud, investigate artificial activity, and enforce terms and partner policies.",
      "We may use information to send service, legal, security, billing, product, support, and policy communications. Marketing communications can be opted out of where available.",
    ],
  },
  {
    title: "Legal bases for processing",
    body: [
      "Where privacy law requires a legal basis, we process information to perform a contract, provide requested services, comply with legal obligations, protect vital or public interests where applicable, pursue legitimate business interests, and with consent when required.",
      "Legitimate interests may include platform security, fraud prevention, customer support, product improvement, analytics, rights administration, payment administration, catalog operations, legal compliance, and business communications.",
    ],
  },
  {
    title: "How we share information",
    body: [
      "We do not sell personal information. We share information when needed to operate services, complete requested workflows, process payments, deliver content, report royalties, comply with law, protect rights and safety, resolve disputes, prevent abuse, or support a business transaction.",
      "We may share information with hosting providers, authentication providers, storage providers, analytics tools, email and communications vendors, customer support tools, payment processors, tax providers, identity verification providers, fraud-prevention vendors, legal advisors, and other service providers.",
      "We may share catalog, metadata, artist, label, rights, payout, reporting, and content information with stores, streaming services, social platforms, content identification systems, collection partners, licensing partners, distribution partners, and other operational partners as needed to provide distribution and related services.",
    ],
  },
  {
    title: "Distribution partner data",
    body: [
      "When you use distribution or catalog services, information may be transmitted to digital service providers, streaming platforms, download stores, social platforms, rights systems, royalty systems, and related partners. Those partners may process information under their own policies and requirements.",
      "Partner reports may include streams, sales, territories, platforms, revenue, deductions, identifiers, fraud indicators, takedown statuses, claim statuses, and other catalog or usage data. We use that data for reporting, analytics, payment, compliance, dispute resolution, and platform operations.",
    ],
  },
  {
    title: "Fraud, safety, and rights protection",
    body: [
      "We process information to detect, prevent, investigate, and respond to artificial streaming, bot activity, abnormal engagement, payment fraud, account abuse, copyright infringement, impersonation, metadata abuse, security incidents, and violations of BNE Studio or partner policies.",
      "This may involve reviewing account activity, catalog history, payment patterns, partner reports, device and network signals, rights documentation, support communications, and related records.",
      "We may share relevant information with stores, payment providers, rights holders, claimants, legal advisors, law enforcement, or other parties when reasonably necessary to protect users, rights holders, partners, BNE Studio, or the public.",
    ],
  },
  {
    title: "Cookies, analytics, and preferences",
    body: [
      "We use cookies and similar technologies to keep you signed in, remember settings, secure sessions, measure performance, understand feature usage, diagnose errors, and improve products.",
      "You can control cookies through browser settings. Some features may not work correctly if required cookies or local storage are disabled.",
      "Where required by law, we will provide additional consent or preference controls for certain analytics, advertising, or tracking technologies.",
    ],
  },
  {
    title: "Retention",
    body: [
      "We keep information for as long as needed to provide services, administer accounts, process royalties and payouts, maintain catalog and rights records, resolve disputes, enforce agreements, prevent fraud, support audits, and comply with legal, tax, accounting, payment, and partner obligations.",
      "Retention periods vary by data type. Catalog, rights, royalty, tax, payment, fraud, dispute, and compliance records may be retained longer than general marketing or support records because music distribution and royalty accounting can require long-term records.",
      "When information is no longer needed, we may delete, anonymize, aggregate, or archive it according to operational and legal requirements.",
    ],
  },
  {
    title: "Security",
    body: [
      "We use reasonable administrative, technical, and organizational safeguards designed to protect information against unauthorized access, loss, misuse, alteration, or disclosure.",
      "No internet transmission, platform, storage provider, or payment system can be guaranteed completely secure. You are responsible for protecting your login credentials and promptly notifying us of suspected unauthorized access.",
    ],
  },
  {
    title: "International transfers",
    body: [
      "BNE Studio and its service providers may process information in the United States and other countries where we, our providers, or our partners operate.",
      "If information is transferred internationally, we use measures required by applicable law, which may include contractual protections, transfer safeguards, or other compliance mechanisms.",
    ],
  },
  {
    title: "Your privacy rights",
    body: [
      "Depending on where you live, you may have rights to request access, correction, deletion, portability, restriction, objection, withdrawal of consent, or information about how personal information is processed.",
      "You may also have rights to opt out of certain marketing, targeted advertising, sale, or sharing practices. BNE Studio does not sell personal information for money.",
      "Some requests may be limited by legal, security, rights-management, royalty accounting, tax, payment, fraud-prevention, dispute, or contractual obligations.",
    ],
  },
  {
    title: "California and US state privacy notices",
    body: [
      "Residents of certain US states may have additional privacy rights, including rights to know, access, correct, delete, obtain a copy, and opt out of certain processing activities.",
      "We may collect categories of personal information such as identifiers, commercial information, internet or network activity, geolocation approximations, professional or business information, financial or payment-related information, user-generated content, audio or visual content you submit, and inferences related to service usage.",
      "We use and disclose those categories for the business and commercial purposes described in this policy. We do not knowingly sell or share personal information of children under 16.",
    ],
  },
  {
    title: "EEA, UK, and international rights",
    body: [
      "If you are located in the EEA, UK, Switzerland, or another jurisdiction with similar privacy rights, you may have rights to access, rectify, erase, restrict, object to processing, transfer data, and lodge a complaint with a supervisory authority.",
      "Where processing is based on consent, you may withdraw consent at any time, but withdrawal does not affect processing that occurred before withdrawal or processing based on another lawful basis.",
    ],
  },
  {
    title: "Authorized agents and verification",
    body: [
      "We may need to verify your identity, account control, authorization, or residency before responding to a privacy request. If an authorized agent submits a request for you, we may require proof of authorization and may still ask you to verify your identity directly.",
      "We will not discriminate against you for exercising privacy rights, but certain requests may affect our ability to provide parts of the service.",
    ],
  },
  {
    title: "Children and minors",
    body: [
      "BNE Studio is not directed to children under 13, and we do not knowingly collect personal information from children under 13.",
      "If you believe a child has provided personal information to BNE Studio, contact us so we can review and take appropriate action.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this Privacy Policy as BNE Studio, our partners, our legal obligations, or our privacy practices change. The updated version will be posted on this page with a revised date.",
      "If changes are material, we may provide additional notice through the site, dashboard, email, or other appropriate channels.",
    ],
  },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Privacy Policy - BNE Studio";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout>
      <main className="legal-page">
        <header className="legal-hero">
          <div className="wrap">
            <span className="eyebrow dot">Privacy</span>
            <h1 className="grad-text">Privacy Policy</h1>
            <p>
              This policy explains how BNE Studio collects, uses, shares, and
              protects information across bne.studio and related BNE Studio
              products and services, including distribution, catalog, reporting,
              payout, creator, link, contract, event, and support workflows.
            </p>
            <div className="legal-meta">
              <span>Last updated: {updatedAt}</span>
              <Link to="/contact-us">Privacy questions</Link>
            </div>
          </div>
        </header>

        <section className="legal-section">
          <div className="wrap legal-grid legal-grid-single">
            <div className="legal-stack">
              {sections.map((section) => (
                <article className="legal-card" key={section.title}>
                  <h3>{section.title}</h3>
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </article>
              ))}

              <article className="legal-card legal-callout">
                <h3>Contact</h3>
                <p>
                  For privacy requests, rights requests, or questions about this
                  policy, contact BNE Studio through the contact page.
                </p>
                <Link className="btn btn-primary" to="/contact-us">
                  Contact us
                </Link>
              </article>
            </div>
          </div>
        </section>
      </main>
    </NightLayout>
  );
}
